import { useEffect, useRef, useState } from 'react';
import { createChart, type IChartApi, type ISeriesApi, type LineData } from 'lightweight-charts';
import { dashboardApi, type DashboardData } from '../api.ts';
import { formatGbp, formatMonthYear, deltaClass, formatDelta } from '../lib/format.ts';

export default function Dashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lockingMonth, setLockingMonth] = useState(false);
  const [lockMessage, setLockMessage] = useState('');

  // Inline editing state for cash / credit card
  const [cashEdit, setCashEdit] = useState('');
  const [ccEdit, setCcEdit] = useState('');
  const [saving, setSaving] = useState(false);

  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<IChartApi | null>(null);
  const lineInstance = useRef<ISeriesApi<'Line'> | null>(null);

  useEffect(() => {
    dashboardApi
      .get()
      .then((d) => {
        setData(d);
        setCashEdit(String(d.assets.cash_savings_gbp));
        setCcEdit(String(d.liabilities.credit_card_gbp));
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  // Build net worth trend chart from snapshots
  useEffect(() => {
    if (!data || !chartRef.current) return;

    // Aggregate snapshots by date into a single net worth value
    const byDate: Record<string, number> = {};
    for (const snap of data.snapshots) {
      byDate[snap.snapshot_date] = (byDate[snap.snapshot_date] ?? 0) + snap.value_gbp;
    }

    const seriesData: LineData[] = Object.entries(byDate)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, value]) => ({ time: date as `${number}-${number}-${number}`, value }));

    if (seriesData.length === 0) return;

    if (!chartInstance.current) {
      chartInstance.current = createChart(chartRef.current, {
        layout: { background: { color: 'transparent' }, textColor: '#9ca3af' },
        grid: { vertLines: { color: '#1f2937' }, horzLines: { color: '#1f2937' } },
        rightPriceScale: { borderColor: '#374151' },
        timeScale: { borderColor: '#374151', timeVisible: true },
        width: chartRef.current.clientWidth,
        height: 200,
      });
      lineInstance.current = chartInstance.current.addLineSeries({
        color: '#0ea5e9',
        lineWidth: 2,
        priceFormat: { type: 'price', precision: 0, minMove: 1 },
      });
    }

    lineInstance.current?.setData(seriesData);
    chartInstance.current?.timeScale().fitContent();
  }, [data]);

  // Resize chart on window resize
  useEffect(() => {
    const el = chartRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      chartInstance.current?.resize(el.clientWidth, 200);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  async function handleLockMonth() {
    setLockingMonth(true);
    setLockMessage('');
    try {
      const res = await dashboardApi.lockMonth();
      setLockMessage(`Snapshot saved for ${formatMonthYear(res.snapshot_date)}`);
      // Refresh data
      const fresh = await dashboardApi.get();
      setData(fresh);
    } catch (e: unknown) {
      setLockMessage(e instanceof Error ? e.message : 'Failed to lock month');
    } finally {
      setLockingMonth(false);
    }
  }

  async function handleSaveManual() {
    setSaving(true);
    try {
      await dashboardApi.updateSettings({
        cash_savings_gbp: parseFloat(cashEdit) || 0,
        credit_card_gbp: parseFloat(ccEdit) || 0,
      });
      const fresh = await dashboardApi.get();
      setData(fresh);
    } catch {
      // silently fail for now
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorState message={error} />;
  if (!data) return null;

  const { totals, assets, liabilities } = data;

  return (
    <div className="space-y-8">
      {/* Page title */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Net Worth</h1>
        <div className="flex items-center gap-3">
          {lockMessage && <span className="text-sm text-emerald-400">{lockMessage}</span>}
          <button
            onClick={handleLockMonth}
            disabled={lockingMonth}
            className="bg-gray-800 hover:bg-gray-700 disabled:opacity-50 text-sm text-gray-200 font-medium px-4 py-2 rounded-lg transition-colors border border-gray-700"
          >
            {lockingMonth ? 'Saving…' : 'Lock this month'}
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard label="Total Assets" value={totals.total_assets} />
        <SummaryCard label="Total Liabilities" value={totals.total_liabilities} negative />
        <SummaryCard label="Net Worth (inc. house)" value={totals.net_worth_inc_property} highlight />
        <SummaryCard label="Net Worth (exc. house)" value={totals.net_worth_exc_property} />
      </div>

      {/* Trend chart */}
      {data.snapshots.length > 0 && (
        <section className="bg-gray-900 rounded-xl border border-gray-800 p-6">
          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Net Worth Trend</h2>
          <div ref={chartRef} />
        </section>
      )}

      {/* Breakdown */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Assets */}
        <section className="bg-gray-900 rounded-xl border border-gray-800 p-6">
          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Assets</h2>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-gray-800">
              {/* Pensions */}
              {Object.entries(assets.pensions).map(([owner, value]) => (
                <BreakdownRow
                  key={`pension-${owner}`}
                  label={`${capitalise(owner)} Pensions`}
                  value={value}
                  badge="pension"
                />
              ))}
              {/* ISAs */}
              {assets.isas.map((p) => (
                <BreakdownRow key={p.id} label={p.name} value={p.value_gbp} badge="isa" />
              ))}
              {assets.nora_jisa_gbp !== null && (
                <BreakdownRow label="Nora JISA" value={assets.nora_jisa_gbp} badge="isa" />
              )}
              {/* GIA */}
              {assets.gia.map((p) => (
                <BreakdownRow key={p.id} label={p.name} value={p.value_gbp} badge="gia" />
              ))}
              {/* Crypto */}
              {assets.crypto.map((p) => (
                <BreakdownRow key={p.id} label={p.name} value={p.value_gbp} badge="crypto" />
              ))}
              {/* Cash */}
              <tr>
                <td className="py-3 text-gray-300">
                  Cash Savings
                  <span className="ml-2 text-xs bg-gray-800 text-gray-400 px-2 py-0.5 rounded">cash</span>
                </td>
                <td className="py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <span className="text-gray-500 text-xs">£</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={cashEdit}
                      onChange={(e) => setCashEdit(e.target.value)}
                      className="w-28 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-right text-white text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-500"
                    />
                  </div>
                </td>
              </tr>
              {/* Property */}
              <BreakdownRow label="Property" value={assets.property_gbp} badge="property" />
            </tbody>
          </table>
        </section>

        {/* Liabilities */}
        <section className="bg-gray-900 rounded-xl border border-gray-800 p-6">
          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Liabilities</h2>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-gray-800">
              {liabilities.mortgages.map((m) => (
                <BreakdownRow
                  key={m.id}
                  label={`${m.label}${m.lender ? ` (${m.lender})` : ''}`}
                  value={m.balance}
                  negative
                  badge="mortgage"
                />
              ))}
              {/* Credit card */}
              <tr>
                <td className="py-3 text-gray-300">
                  Credit Card
                  <span className="ml-2 text-xs bg-gray-800 text-gray-400 px-2 py-0.5 rounded">credit</span>
                </td>
                <td className="py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <span className="text-gray-500 text-xs">£</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={ccEdit}
                      onChange={(e) => setCcEdit(e.target.value)}
                      className="w-28 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-right text-white text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-500"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          {/* Save manual fields button */}
          <div className="mt-4 flex justify-end">
            <button
              onClick={handleSaveManual}
              disabled={saving}
              className="text-xs bg-brand-700 hover:bg-brand-600 disabled:opacity-50 text-white px-4 py-2 rounded-lg transition-colors"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function SummaryCard({
  label,
  value,
  negative,
  highlight,
}: {
  label: string;
  value: number;
  negative?: boolean;
  highlight?: boolean;
}) {
  return (
    <div
      className={[
        'rounded-xl border p-5',
        highlight
          ? 'bg-brand-900/30 border-brand-700/50'
          : 'bg-gray-900 border-gray-800',
      ].join(' ')}
    >
      <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">{label}</p>
      <p
        className={[
          'text-xl font-bold tabular-nums',
          highlight ? 'text-brand-400' : negative ? 'text-red-400' : 'text-white',
        ].join(' ')}
      >
        {negative ? '-' : ''}{formatGbp(Math.abs(value))}
      </p>
    </div>
  );
}

const BADGE_COLOURS: Record<string, string> = {
  pension:  'bg-purple-900/60 text-purple-300',
  isa:      'bg-blue-900/60 text-blue-300',
  gia:      'bg-amber-900/60 text-amber-300',
  crypto:   'bg-orange-900/60 text-orange-300',
  property: 'bg-emerald-900/60 text-emerald-300',
  mortgage: 'bg-red-900/60 text-red-300',
  cash:     'bg-gray-800 text-gray-400',
};

function BreakdownRow({
  label,
  value,
  badge,
  negative,
}: {
  label: string;
  value: number;
  badge?: string;
  negative?: boolean;
}) {
  return (
    <tr>
      <td className="py-3 text-gray-300">
        {label}
        {badge && (
          <span className={`ml-2 text-xs px-2 py-0.5 rounded ${BADGE_COLOURS[badge] ?? 'bg-gray-800 text-gray-400'}`}>
            {badge}
          </span>
        )}
      </td>
      <td className={`py-3 text-right font-mono tabular-nums font-medium ${negative ? 'text-red-400' : 'text-white'}`}>
        {negative ? '-' : ''}{formatGbp(Math.abs(value))}
      </td>
    </tr>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="bg-red-950/40 border border-red-900/50 rounded-xl p-6 text-red-400">
      Failed to load dashboard: {message}
    </div>
  );
}

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Suppress unused warning — deltaClass is available for future use
void (deltaClass as unknown);
void (formatDelta as unknown);
